import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatDate } from "@/lib/catalog";
import { PageHead, CardSkeleton, ErrorState, EmptyState } from "@/components/Ui";
import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";

export default function Blog() {
  const { t, lang } = useLang();
  const [state, setState] = useState({ posts: null, loading: true, error: null });

  const load = () => {
    setState({ posts: null, loading: true, error: null });
    apiGet("/blog")
      .then((posts) => setState({ posts, loading: false, error: null }))
      .catch((error) => setState({ posts: null, loading: false, error }));
  };
  useEffect(load, []);

  const { posts, loading, error } = state;

  return (
    <div className="bg-grid" data-testid="blog-page">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <PageHead eyebrow={t("blog.eyebrow")} title={t("blog.title")} sub={t("blog.sub")} testid="blog-head" />
        <div className="mt-12">
          {loading ? (
            <CardSkeleton count={3} />
          ) : error ? (
            <ErrorState onRetry={load} />
          ) : !posts || posts.length === 0 ? (
            <EmptyState message={t("blog.empty")} testid="blog-empty" />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 80}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="mg-card group flex h-full flex-col overflow-hidden transition-shadow hover:shadow-lg hover:shadow-marine/5"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {p.cover_url ? (
                      <img src={p.cover_url} alt="" className="h-44 w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-44 w-full items-center justify-center bg-marine">
                        <img src="/brand/icon.png" alt="" className="h-14 w-14 opacity-60" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      {p.published_at && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-gold">
                          {formatDate(p.published_at, lang)}
                        </p>
                      )}
                      <h3 className="mt-2 font-display text-lg font-bold text-marine group-hover:text-brand">
                        {lang === "fr" ? p.title_fr : p.title_en}
                      </h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                        {lang === "fr" ? p.excerpt_fr : p.excerpt_en}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                        {t("blog.readMore")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
